import {
  TellescopeModelFromName,
  useCachedTags,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
} from "@tellescope/tanstack-query";
import { Button, cn, SearchInput, Spinner, useDebouncedValue } from "@tellescope/ui";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LocationCard } from "./LocationCard";

export interface LocationsViewProps {
  getLocationOnClick: (
    location: TellescopeModelFromName<"appointment_locations">
  ) => (() => void) | { to: string; replace?: boolean };
  onDelete: (location: TellescopeModelFromName<"appointment_locations">) => void;
  onAddLocationClick: () => void;
}

export const LocationsView = ({ getLocationOnClick, onDelete, onAddLocationClick }: LocationsViewProps) => {
  const { t } = useTranslation("calendar");

  const [locationSearchQuery, setLocationSearchQuery] = useState("");
  const [debouncedLocationSearch] = useDebouncedValue(locationSearchQuery);
  const [selectedLocationTags, setSelectedLocationTags] = useState<Set<string>>(new Set<string>());

  const {
    create: { access: canCreate },
    update: { access: canUpdate },
  } = usePermissionQuery({
    model: "appointment_locations",
    actions: ["create", "update"],
  });

  const locationsQuery = useTellescopeInfiniteQuery({
    model: "appointment_locations",
    method: "getSome",
    request: {
      search: debouncedLocationSearch
        ? {
            query: debouncedLocationSearch,
          }
        : undefined,
      mdbFilter: selectedLocationTags.size ? { tags: { $in: Array.from(selectedLocationTags) } } : {},
    },
    fetchAllPages: true,
    select: (data) => data.pages.flat(),
    placeholderData: (prev) => prev,
  });
  const { data: locationTags } = useCachedTags({ model: "appointment_locations" });

  return (
    <>
      <section data-testid="locations-page" className="shrink-0 p-6 border-b flex justify-between items-center">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold">{t("Locations")}</h1>
          <h3 className="font-light text-muted-foreground" data-testid="locations-count">
            {t("{{count}} locations", { count: locationsQuery.data?.length ?? 0 })}
          </h3>
        </div>
        {canCreate && (
          <Button className="font-light" onClick={onAddLocationClick} data-testid="locations-add-button">
            <Plus />
            {t("Add location")}
          </Button>
        )}
      </section>
      <section className="overflow-y-auto p-6 @container">
        <div className="space-y-3 pb-4 border-b">
          <SearchInput
            wrapperClassName="max-w-[400px]"
            placeholder={t("Search locations...")}
            value={locationSearchQuery}
            onChange={(e) => setLocationSearchQuery(e.target.value)}
            data-testid="locations-search-input"
          />
          <div className="flex items-center gap-2" data-testid="locations-tag-filters">
            {locationTags.map((tag) => (
              <Button
                key={tag}
                data-testid={`locations-tag-filter-${tag}`}
                variant={selectedLocationTags.has(tag) ? "default" : "outline"}
                className={cn(
                  "rounded-full text-xs font-light h-auto py-1",
                  selectedLocationTags.has(tag) ? "border" : "text-muted-foreground"
                )}
                onClick={() =>
                  setSelectedLocationTags((prev) => {
                    const next = new Set(prev);
                    if (prev.has(tag)) {
                      next.delete(tag);
                      return next;
                    } else {
                      next.add(tag);
                      return next;
                    }
                  })
                }
              >
                {tag}
              </Button>
            ))}
            {!!selectedLocationTags.size && (
              <Button
                variant="ghost"
                className="h-auto py-1 rounded-full text-xs font-light text-muted-foreground"
                onClick={() => setSelectedLocationTags(new Set<string>())}
                data-testid="locations-tag-filter-clear"
              >
                {t("Clear")}
              </Button>
            )}
          </div>
        </div>
        {locationsQuery.isLoading ? (
          <div className="flex flex-col items-center gap-2" data-testid="locations-loading">
            <Spinner />
            <p className="text-muted-foreground text-sm">{t("Loading...")}</p>
          </div>
        ) : (
          <div
            data-testid="locations-grid"
            className="grid grid-cols-1 @2xl:grid-cols-2 @4xl:grid-cols-3 @7xl:grid-cols-4 gap-4 py-4"
          >
            {locationsQuery.data?.map((location) => (
              <LocationCard
                key={location.id}
                location={location}
                onClick={canUpdate ? getLocationOnClick(location) : undefined}
                onDelete={() => onDelete(location)}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};
